/**
 * What is pinned in the private thread: Continue (the recorded talk they are partway through, with
 * its offer) and the pins, newest first. Also the calendar menu and the offer, which the page shares.
 */
import type { ReactNode } from "react";
import { api } from "../../lib/api";
import { daysUntil, labelParts } from "../../../shared/live-time";
import type { WatchPin, WatchSession } from "../../../shared/types";
import { fileHref, withEmail } from "../WatchOffer";
import { usePage } from "./context";
import { IcCal, IcCalSub, IcCDown, IcCheck, IcCUp, IcDl, IcPlay, IcX } from "./icons";
import { clock, fileHref as attHref, pinDay, pinTarget, snippet, talkLength } from "./model";
import { shownPins } from "./pins";

const localZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** "Tomorrow at 6:00 PM", "Today at 6:00 PM", "Thursday at 6:00 PM", "Tue, Nov 10 at 6:00 PM", on their own clock. */
function whenWords(ms: number, nowMs: number): { long: string; short: string } {
  const iso = new Date(ms).toISOString();
  const p = labelParts(iso, undefined, "short");
  const l = labelParts(iso, undefined, "long");
  const days = daysUntil(iso, localZone(), nowMs) ?? 99;
  const time = p?.time.replace(" ", " ") ?? "";
  const day = days === 0 ? "Today" : days === 1 ? "Tomorrow" : days > 1 && days < 7 ? (l?.weekday ?? "") : p ? `${p.weekday}, ${p.month} ${p.day}` : "";
  const short = days === 0 ? "Today" : days === 1 ? "Tomorrow" : p ? `${p.month} ${p.day}` : "";
  return { long: `${day} at ${time}`, short: `${short}, ${time}` };
}

/** The way into a room: its seat link, which opens once the room does. */
export const seatHref = (room: WatchSession, token: string) => `/w/${encodeURIComponent(room.slug)}/watch?t=${encodeURIComponent(token)}`;

/** Add a live to the calendar: the one session, or the whole series when it repeats. */
export function useCalendarMenu(): (anchor: HTMLElement, room: WatchSession) => void {
  const { openMenu, inCalendar, markCalendar, toast, nowMs } = usePage();
  return (anchor, room) => {
    const cal = room.calendar;
    if (!cal) return;
    if (inCalendar(room.webinar_id)) {
      toast("Already in your calendar.");
      return;
    }
    const series = room.live?.series;
    const startsAt = room.live ? Date.parse(room.live.startsAt) : nowMs;
    const add = () => {
      const a = document.createElement("a");
      a.href = cal.ics;
      a.download = "";
      document.body.append(a);
      a.click();
      a.remove();
      markCalendar(room.webinar_id);
      toast("Added to your calendar.");
    };
    openMenu({
      anchor: anchor.getBoundingClientRect(),
      items: [
        { key: "add", icon: <IcCal s={18} />, label: "Add to calendar", sub: series ? series.long : whenWords(startsAt, nowMs).long, run: add },
        ...(cal.subscribe
          ? [
              {
                key: "sub",
                icon: <IcCalSub s={18} />,
                label: "Subscribe to the calendar",
                sub: series?.long,
                run: () => {
                  window.location.href = cal.subscribe as string;
                  markCalendar(room.webinar_id);
                  toast("Subscribed to the calendar.");
                },
              },
            ]
          : []),
      ],
    });
  };
}

/** Whether a talk's offer has landed: as in the room, once the talk has played to its moment. */
const offerShown = (room: WatchSession) => {
  const o = room.offer;
  if (!o || o.kind === "message") return false;
  return !(o.appears_at && room.watched_sec < o.appears_at);
};

/** The offer from a talk: the same booking, checkout, file or code the room's offer gives. */
export function OfferLink({ room, className = "plink" }: { room: WatchSession; className?: string }): ReactNode {
  const { token, me, toast } = usePage();
  const offer = room.offer;
  if (!offer || !offerShown(room)) return null;
  const convert = () => {
    api.watch.signup(token, { webinar_id: room.webinar_id }).catch(() => {});
  };
  if (offer.kind === "code")
    return (
      <button
        type="button"
        className={className}
        onClick={() => {
          convert();
          try {
            void navigator.clipboard?.writeText(offer.code ?? "").catch(() => {});
          } catch {
            /* the code is on screen either way */
          }
          toast(`Copied ${offer.code ?? ""}`);
        }}
      >
        <IcCal s={16} />
        <span>{offer.label || offer.code}</span>
      </button>
    );
  const href = offer.kind === "download" ? (offer.file ? fileHref(offer.file) : null) : offer.url ? withEmail(offer.url, me.email) : null;
  if (!href) return null;
  return (
    <a className={className} href={href} target="_blank" rel="noopener noreferrer" onClick={convert}>
      <IcCal s={16} />
      <span>{offer.label || "Open"}</span>
    </a>
  );
}

/** "Continue from 12:04", "Start the talk", "Watch again". */
export function continueWords(room: WatchSession): string {
  if (room.watched_sec <= 0) return "Start the talk";
  if (room.watched_sec >= room.duration_sec - 5) return "Watch again";
  return `Continue from ${clock(room.watched_sec)}`;
}

export function ContinueCard(): ReactNode {
  const { resume: talk, playTalk, host } = usePage();
  if (!talk) return null;
  const pct = talk.duration_sec > 0 ? Math.min(100, (talk.watched_sec / talk.duration_sec) * 100) : 0;
  return (
    <section className="pc pc-work" aria-label="Continue the talk">
      <div className="pcw">
        <button type="button" className="pth" aria-label="Continue the talk" onClick={() => playTalk(talk.webinar_id, { start: true })}>
          {talk.poster_url ? <img src={talk.poster_url} alt="" /> : null}
          <i style={{ width: `${pct}%` }} />
          <u>
            <IcPlay s={14} />
          </u>
        </button>
        <span className="ptx">
          <span className="pe">Continue</span>
          <b dir="auto">{talk.title}</b>
          <span className="psub">
            {clock(talk.watched_sec)} of {talkLength(talk.duration_sec)}
            {talk.chapters.length ? (
              <>
                {" · "}
                <button type="button" className="tlink" onClick={() => playTalk(talk.webinar_id)}>
                  Chapters
                </button>
              </>
            ) : null}
          </span>
        </span>
      </div>
      <div className="pcw-b">
        <button type="button" className="btn btn-p" onClick={() => playTalk(talk.webinar_id, { start: true })}>
          <IcPlay s={15} />
          {continueWords(talk)}
        </button>
      </div>
      {offerShown(talk) ? (
        <div className="pcw-offer">
          <span className="pe"><bdi>{host.first}</bdi>’s offer from this talk</span>
          <OfferLink room={talk} />
        </div>
      ) : null}
    </section>
  );
}

function PinRow({ pin }: { pin: WatchPin }): ReactNode {
  const { convo, files, host, me, nowMs, seePin, goTo, unpin, saved, markSaved } = usePage();
  const t = pinTarget(pin, convo, files);
  const dot = pin.seen ? null : <i className="pdot" aria-label="New" />;
  const meta = `${pin.by === "host" ? host.first : "You"} · ${pinDay(pin.created_at, Math.floor(nowMs / 1000))}`;
  const un = (
    <button type="button" className="pun" aria-label="Unpin" title="Unpin" onClick={() => unpin(pin)}>
      <IcX s={13} />
    </button>
  );
  const fileAtt = t.entry?.post?.kind === "file" ? t.entry.post.attachments.find((a) => a.kind === "file") : undefined;
  const asFile = t.file ?? (fileAtt && t.entry ? { key: `${t.entry.id}:0`, title: t.entry.post?.title || fileAtt.name, href: attHref(fileAtt), from: "", at: t.entry.at } : undefined);
  if (asFile) {
    const f = asFile;
    const done = saved(f.key);
    const take = () => {
      seePin(pin);
      markSaved(f.key);
    };
    return (
      <div className="pr" data-pid={pin.id}>
        <a className="pr-go" href={f.href ?? undefined} download tabIndex={-1} aria-hidden="true" onClick={take}>
          <span className="pr-ic">
            <span className="fmini">
              <i />
              <i />
              <i />
              <i />
            </span>
            {dot}
          </span>
          <span className="pr-x">
            <b dir="auto">{pin.title || f.title}</b>
            <small>{meta}</small>
          </span>
        </a>
        <a className={`dl ic${done ? " saved" : ""}`} href={f.href ?? undefined} download aria-label={done ? "Saved. Download again" : `Download ${f.title}`} onClick={take}>
          {done ? <IcCheck s={15} /> : <IcDl s={15} />}
        </a>
        {un}
      </div>
    );
  }
  const e = t.entry;
  if (!e) return null;
  const video = (e.post?.attachments ?? e.message?.attachments ?? []).find((a) => a.kind === "video" && (a.media_key || a.media_url));
  const face =
    e.from === "host" ? (
      host.avatar ? (
        <img src={host.avatar} alt="" />
      ) : (
        <span className="pr-ini">{host.first.slice(0, 1)}</span>
      )
    ) : me.avatar ? (
      <img src={me.avatar} alt="" />
    ) : (
      <span className="pr-ini">{me.first.slice(0, 1)}</span>
    );
  return (
    <div className="pr" data-pid={pin.id}>
      <button
        type="button"
        className="pr-go"
        onClick={() => {
          seePin(pin);
          goTo(e.id);
        }}
      >
        {video ? (
          <span className="pr-ic vid">
            {video.poster_url ? <img src={video.poster_url} alt="" /> : null}
            <u>
              <IcPlay s={12} />
            </u>
            {dot}
          </span>
        ) : (
          <span className="pr-ic face">
            {face}
            {dot}
          </span>
        )}
        <span className="pr-x">
          <b>{pin.title || snippet(e)}</b>
          <small>{meta}</small>
        </span>
      </button>
      {un}
    </div>
  );
}

/** The iPad upright's one line for what the thread keeps beside it: the pins, the talk to continue and the next live, opening the list. */
export function PinLine(): ReactNode {
  const { pins, convo, files, resume, guest, openSheet } = usePage();
  const newest = pins[0];
  const count = pins.length + (resume ? 1 : 0) + (guest.nextLive ? 1 : 0);
  if (count === 0) return null;
  const t = newest ? pinTarget(newest, convo, files) : null;
  const title = newest ? newest.title || t?.file?.title || (t?.entry ? snippet(t.entry) : "") : resume ? resume.title : "Next live";
  return (
    <button type="button" className="jp-pinline" aria-haspopup="dialog" onClick={() => openSheet("pins")}>
      {pins.some((p) => !p.seen) ? <i className="pdot" aria-label="New" /> : null}
      <b>Pinned · {count}</b>
      <span dir="auto">{title}</span>
      <IcCDown s={13} />
    </button>
  );
}

export function PinList(): ReactNode {
  const { pins, pinsAll, togglePinsAll } = usePage();
  if (!pins.length) return null;
  const { shown, older } = shownPins(pins, pinsAll);
  const olderNew = older > 0 && pins.slice(shown.length).some((p) => !p.seen);
  return (
    <div className="plist" role="group" aria-label="Pins">
      {shown.map((p) => (
        <PinRow key={p.id} pin={p} />
      ))}
      {pins.length > 4 ? (
        <button type="button" className="pold" aria-expanded={pinsAll} onClick={togglePinsAll}>
          {pinsAll ? (
            <>
              Hide older
              <IcCUp s={13} />
            </>
          ) : (
            <>
              {olderNew ? <i className="pdot" /> : null}
              {older} older
              <IcCDown s={13} />
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}
